import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import RevaText from "components/common/RevaText";
import AlertDialog from "components/common/AlertDialog";
import SortableList from "components/common/SortableList";
import performRequest from "../../../network/perform-request";
import {
  competitionCustomFieldsDestroyRoute,
  competitionCustomFieldsIndexRoute,
  competitionCustomFieldsReorderRoute,
  competitionsShowRoute,
} from "../../../network/api-routes";
import CustomFieldFormModal from "./CustomFieldFormModal";

const TYPE_LABEL = {
  short_text: "Texto corto",
  long_text: "Texto largo",
  single_select: "Selección única",
  boolean: "Sí / No",
};

const SCOPE_LABEL = {
  registration: "Por inscripción",
  player: "Por jugador",
};

const CompetitionCustomFields = ({ userContext, competitionUuid: competitionUuidProp }) => {
  const intl = useIntl();
  const { id: competitionUuidFromParams } = useParams();
  const competitionUuid = competitionUuidProp || competitionUuidFromParams;
  const { enqueueSnackbar } = useSnackbar();
  const user = userContext?.user;

  const [ready, setReady] = useState(false);
  const [competition, setCompetition] = useState(null);
  const [tournaments, setTournaments] = useState([]);
  const [fields, setFields] = useState([]);
  const [previewTournamentId, setPreviewTournamentId] = useState("all");

  const [modalOpen, setModalOpen] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [toArchive, setToArchive] = useState(null);

  const competitionId = competition?.id;
  const isPreviewMode = previewTournamentId !== "all";

  const fetchCompetition = useCallback(async () => {
    const res = await performRequest("POST", competitionsShowRoute, { competition_uuid: competitionUuid }, true, user);
    const comp = res.data.data;
    setCompetition(comp);
    setTournaments(comp.tournaments?.data || []);
    return comp;
  }, [competitionUuid, user]);

  const fetchFields = useCallback(
    async (compId, tournamentId = null) => {
      const params = { competition_id: compId };
      if (tournamentId) params.tournament_id = tournamentId;
      const res = await performRequest("POST", competitionCustomFieldsIndexRoute, params, true, user);
      setFields(res.data?.data || []);
    },
    [user],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setReady(false);
      try {
        const comp = await fetchCompetition();
        if (cancelled) return;
        await fetchFields(comp.id, null);
      } catch (e) {
        console.error(e);
        enqueueSnackbar(intl.formatMessage({ id: "No se pudieron cargar los campos personalizados" }), {
          variant: "error",
        });
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competitionUuid]);

  useEffect(() => {
    if (!competitionId) return;
    fetchFields(competitionId, isPreviewMode ? previewTournamentId : null).catch((e) => console.error(e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewTournamentId, competitionId]);

  const tournamentsById = useMemo(() => {
    const map = {};
    tournaments.forEach((t) => {
      map[t.id] = t;
    });
    return map;
  }, [tournaments]);

  const handleNew = () => {
    setEditingField(null);
    setModalOpen(true);
  };

  const handleEdit = (field) => {
    setEditingField(field);
    setModalOpen(true);
  };

  const handleSaved = async () => {
    setModalOpen(false);
    setEditingField(null);
    await fetchFields(competitionId, isPreviewMode ? previewTournamentId : null);
  };

  const confirmArchive = async () => {
    if (!toArchive) return;
    try {
      await performRequest("POST", competitionCustomFieldsDestroyRoute, { id: toArchive.id }, true, user);
      enqueueSnackbar(intl.formatMessage({ id: "Campo archivado" }), { variant: "success" });
      setToArchive(null);
      await fetchFields(competitionId, isPreviewMode ? previewTournamentId : null);
    } catch (e) {
      console.error(e);
      enqueueSnackbar(intl.formatMessage({ id: "No se pudo archivar el campo" }), { variant: "error" });
    }
  };

  const handleReorder = async (newList) => {
    if (isPreviewMode) return;
    const newIds = newList.map((f) => f.id);
    const oldIds = fields.map((f) => f.id);
    if (JSON.stringify(newIds) === JSON.stringify(oldIds)) return;
    setFields(newList);
    try {
      await performRequest(
        "POST",
        competitionCustomFieldsReorderRoute,
        { competition_id: competitionId, order: newIds },
        true,
        user,
      );
    } catch (e) {
      console.error(e);
      enqueueSnackbar(intl.formatMessage({ id: "No se pudo reordenar" }), { variant: "error" });
      fetchFields(competitionId, null);
    }
  };

  const renderFieldCard = (field) => {
    const scopeTargetLabel = field.tournament_id
      ? tournamentsById[field.tournament_id]?.name || intl.formatMessage({ id: "Torneo específico" })
      : intl.formatMessage({ id: "Todos los torneos" });

    return (
      <Paper
        key={field.id}
        sx={{
          display: "flex",
          alignItems: "center",
          p: 2,
          mb: 1,
          gap: 2,
        }}
      >
        {!isPreviewMode && (
          <IconButton className="custom-field-drag-handle" sx={{ cursor: "grab" }} size="small">
            <DragHandleIcon />
          </IconButton>
        )}
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            <RevaText variant="h5">{field.label}</RevaText>
            {field.required && <Chip size="small" color="primary" label={intl.formatMessage({ id: "Obligatorio" })} />}
            <Chip
              size="small"
              variant={field.tournament_id ? "filled" : "outlined"}
              color={field.tournament_id ? "secondary" : "default"}
              label={scopeTargetLabel}
            />
          </Stack>
          <Stack direction="row" gap={2} flexWrap="wrap" sx={{ mt: 0.5 }}>
            <RevaText variant="caption" color="text.secondary">
              {intl.formatMessage({ id: TYPE_LABEL[field.type] || field.type })}
            </RevaText>
            <RevaText variant="caption" color="text.secondary">
              {intl.formatMessage({ id: SCOPE_LABEL[field.scope] || field.scope })}
            </RevaText>
            {field.type === "single_select" && field.options?.length > 0 && (
              <RevaText variant="caption" color="text.secondary">
                {field.options.join(" · ")}
              </RevaText>
            )}
          </Stack>
          {field.help_text && (
            <RevaText variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
              {field.help_text}
            </RevaText>
          )}
        </Box>
        <Stack direction="row">
          <Tooltip title={intl.formatMessage({ id: "Editar" })}>
            <IconButton size="small" onClick={() => handleEdit(field)}>
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={intl.formatMessage({ id: "Archivar" })}>
            <IconButton size="small" onClick={() => setToArchive(field)}>
              <ArchiveOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Paper>
    );
  };

  if (!ready && !competition) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={2}
      >
        <Box>
          <RevaText id="Campos personalizados" variant="h2" />
          <RevaText
            id="Preguntas extra que verán los participantes al inscribirse al evento."
            variant="body2"
            color="text.secondary"
          />
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>{intl.formatMessage({ id: "Vista previa" })}</InputLabel>
            <Select
              value={previewTournamentId}
              label={intl.formatMessage({ id: "Vista previa" })}
              onChange={(e) => setPreviewTournamentId(e.target.value)}
            >
              <MenuItem value="all">{intl.formatMessage({ id: "Todos (configuración)" })}</MenuItem>
              {tournaments.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleNew}
            disabled={!competitionId}
          >
            {intl.formatMessage({ id: "Nuevo campo" })}
          </Button>
        </Stack>
      </Stack>

      {isPreviewMode && (
        <RevaText
          id="Estás viendo el conjunto resuelto para este torneo (globales + sus overrides). Cambia a 'Todos' para editar y reordenar."
          variant="caption"
          color="text.secondary"
        />
      )}

      {fields.length === 0 && ready ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <RevaText id="Aún no hay campos personalizados." color="text.secondary" />
        </Paper>
      ) : isPreviewMode ? (
        <Box>{fields.map((field) => renderFieldCard(field))}</Box>
      ) : (
        <SortableList
          items={fields}
          onChange={handleReorder}
          handleClassName=".custom-field-drag-handle"
          renderItem={(field) => renderFieldCard(field)}
        />
      )}

      <CustomFieldFormModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingField(null);
        }}
        onSaved={handleSaved}
        competitionId={competitionId}
        tournaments={tournaments}
        user={user}
        field={editingField}
      />

      <AlertDialog
        hideButton
        isOpen={!!toArchive}
        alertTitle={intl.formatMessage({ id: "Archivar campo" })}
        alertBody={intl.formatMessage({
          id: "Las respuestas existentes se conservan, pero el campo dejará de aparecer en nuevas inscripciones.",
        })}
        confirmButtonTitle={intl.formatMessage({ id: "Archivar" })}
        cancelButtonTitle={intl.formatMessage({ id: "Cancelar" })}
        onConfirm={confirmArchive}
        onCancel={() => setToArchive(null)}
      />
    </Stack>
  );
};

export default CompetitionCustomFields;
